// Deterministic business-name ideas for /tools/business-name-generator.
// Runs in the browser with no network calls: the same inputs always give the
// same list, and "More ideas" just moves to the next seed.

const STYLE_WORDS = {
  studio: { suffixes: ['Studio', 'Studios', 'Atelier', 'Workshop', 'Lab'], prefixes: ['House of', 'Studio'] },
  modern: { suffixes: ['Co.', 'Collective', 'Works', 'HQ', 'Labs'], prefixes: ['Hello', 'Hey', 'Made by'] },
  classic: { suffixes: ['& Co.', 'Group', 'Partners', 'Associates', 'Consulting'], prefixes: ['The'] },
  bold: { suffixes: ['Forge', 'Engine', 'Craft', 'Supply', 'Society'], prefixes: ['Bright', 'True', 'Clear'] },
};

const EVOCATIVE = ['North', 'Harbor', 'Oak', 'Summit', 'Field', 'Lantern', 'Cedar', 'Signal', 'Atlas', 'Ember', 'Bloom', 'Keystone', 'Meridian', 'Juniper', 'Copper', 'Tidal', 'Granite', 'Willow', 'Beacon', 'Fable'];

const TRAILING = ['ly', 'io', 'ify', 'hub', 'wise', 'craft'];

export const titleCase = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

export function cleanWords(value, max = 3) {
  return String(value ?? '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w.length >= 2 && w.length <= 20)
    .slice(0, max)
    .map(titleCase);
}

// Small seeded PRNG (mulberry32) so lists are stable per input and seed.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

const pick = (list, r) => list[Math.floor(r() * list.length)];

// Lowercase letters and digits only; what a .com domain for the name would be.
export const domainSlug = (name) => String(name).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]/g, '');

export function generateNames({ keywords = '', ownName = '', style = 'studio', count = 24, seed = 0 } = {}) {
  const words = cleanWords(keywords);
  const [first, last] = cleanWords(ownName, 2);
  const styleWords = STYLE_WORDS[style] || STYLE_WORDS.studio;
  if (!words.length && !first) return [];

  const r = rng(hash(`${words.join(' ')}|${first || ''}|${last || ''}|${style}`) + seed * 7919);
  const key = words[0] || first;
  const second = words[1];
  const candidates = [];
  const add = (name, kind) => {
    const clean = name.replace(/\s+/g, ' ').trim();
    if (clean.length < 3 || clean.length > 32) return;
    if (!candidates.some((c) => c.name.toLowerCase() === clean.toLowerCase())) candidates.push({ name: clean, kind });
  };

  for (let i = 0; i < count * 4 && candidates.length < count; i += 1) {
    const roll = r();
    if (roll < 0.18) add(`${key} ${pick(styleWords.suffixes, r)}`, 'Descriptive');
    else if (roll < 0.3 && second) add(`${key} & ${second}`, 'Descriptive');
    else if (roll < 0.42) add(`${pick(EVOCATIVE, r)} ${key}`, 'Evocative');
    else if (roll < 0.52) add(`${pick(EVOCATIVE, r)} ${pick(styleWords.suffixes, r)}`, 'Evocative');
    else if (roll < 0.62) add(`${pick(styleWords.prefixes, r)} ${key}`, 'Descriptive');
    else if (roll < 0.72 && first) add(`${first} ${pick(styleWords.suffixes, r)}`, 'Personal');
    else if (roll < 0.8 && last) add(`${last} ${key}`, 'Personal');
    else if (roll < 0.86 && first && last) add(`${first.charAt(0)}${last.charAt(0)} ${pick(styleWords.suffixes, r)}`, 'Personal');
    else if (roll < 0.94) add(`${key}${pick(TRAILING, r)}`, 'Invented');
    else add(`${key} ${pick(EVOCATIVE, r)}`, 'Evocative');
  }
  return candidates.map((c) => ({ ...c, domain: `${domainSlug(c.name)}.com` }));
}

// Neutral, read-only lookups. Nothing is sent unless the visitor clicks.
export const registrationLookupUrl = (domain) => `https://lookup.icann.org/en/lookup?name=${encodeURIComponent(domain)}`;
export const trademarkSearchUrl = () => 'https://tmsearch.uspto.gov/search/search-information';
